import { useQuery } from "@tanstack/react-query";
import { useState, type ReactElement } from "react";
import type { ContactListItem, ListResult } from "@cx/contracts";
import { formatDocument } from "@cx/domain";
import { useSession } from "../../session";

/**
 * Busca de contato para a aba Fornecedores (fornecedor = contato da empresa, RF-CAD-01). Usa a mesma busca FTS da
 * lista de contatos; quem não lê contatos não vê o seletor.
 */
export function SupplierPicker({
  exclude,
  onPick,
}: {
  exclude: string[];
  onPick(c: { id: string; name: string }): void;
}): ReactElement {
  const { companyId, api } = useSession();
  const [q, setQ] = useState("");
  const term = q.trim();
  const found = useQuery({
    queryKey: ["contacts", companyId, { q: term, picker: true }],
    enabled: term.length >= 2,
    queryFn: () =>
      api<ListResult<ContactListItem>>(`/contacts?${new URLSearchParams({ q: term, limit: "8" })}`),
  });
  const rows = (found.data?.data ?? []).filter((c) => !exclude.includes(c.id));
  return (
    <div className="mb-3">
      <label className="form-label" htmlFor="supplier-search">
        Adicionar fornecedor
      </label>
      <input
        id="supplier-search"
        type="search"
        className="form-control"
        placeholder="Digite nome, fantasia ou CNPJ do contato"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      {term.length >= 2 && (
        <ul className="list-group mt-1" aria-label="Contatos encontrados">
          {found.isLoading && <li className="list-group-item text-muted">Buscando…</li>}
          {found.isSuccess && rows.length === 0 && (
            <li className="list-group-item text-muted">Nenhum contato encontrado.</li>
          )}
          {rows.map((c) => (
            <li key={c.id} className="list-group-item d-flex align-items-center gap-2">
              <span className="flex-grow-1">
                {c.name}
                {c.document && <span className="text-muted small ms-2">{formatDocument(c.document)}</span>}
              </span>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                aria-label={`Adicionar ${c.name} como fornecedor`}
                onClick={() => {
                  onPick({ id: c.id, name: c.name });
                  setQ("");
                }}
              >
                Adicionar
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
